import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import GoLiveModal from './GoLiveModal';
import {
  Caption,
  CardBox,
  CardHeader,
  CardTitle,
  IconButton,
  LinkText,
  Notice,
  RowBody,
  RowLabel,
  Rows,
  Segmented,
} from './styles';
import Pill from '../shared/Pill';
import { formatDateTime } from '../shared/format';

import PilotBudgetRows from './PilotBudgetRows';
import { ReloadFailedError, RELOAD_FAILED_MESSAGE } from './useJourneys';
import ConfirmationModal from '@/components/ConfirmationModalV2';
import PermissionComponent from '@/routes/PermissionComponent';
import ROUTES from '@/routes/routes';
import useAuth from '@/hooks/useAuth';
import {
  JourneyChanges,
  JourneysPayload,
  LifecycleMode,
  lifecycleErrorMessage,
} from '@/services/lifecycleAPI';
import { elapsedLabel } from '@/util/relativeTime';

const MODES: { mode: LifecycleMode; label: string; testId: string }[] = [
  { mode: 'off', label: 'Off', testId: 'engineModeOff' },
  { mode: 'shadow', label: 'Shadow', testId: 'engineModeShadow' },
  { mode: 'live', label: 'Live', testId: 'engineModeLive' },
];

const MODE_LINE: Record<LifecycleMode, string> = {
  off: 'Nothing is planned or sent',
  shadow: 'Messages are planned and logged, nothing is sent',
  live: 'Messages are sent to workers in the pilot',
};

// Design 2.1: going to off or shadow asks a short ConfirmationModalV2.
export const STEP_DOWN: Record<
  'off' | 'shadow',
  { title: string; message: string; confirmLabel: string }
> = {
  off: {
    title: 'Turn the engine off?',
    message: 'Nothing is planned or sent while the engine is off.',
    confirmLabel: 'Turn off',
  },
  shadow: {
    title: 'Switch to Shadow mode?',
    message: 'Messages are planned and logged, nothing is sent.',
    confirmLabel: 'Switch to Shadow',
  },
};

type Props = {
  data: JourneysPayload;
  onRefresh: () => Promise<void>;
  onSave: (changes: JourneyChanges) => Promise<void>;
};

const EngineCard = ({ data, onRefresh, onSave }: Props) => {
  const { currentAdminIsTenantAdminRole } = useAuth();
  const { global } = data;
  const current: LifecycleMode = global.mode ?? 'off';
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [goLiveOpen, setGoLiveOpen] = useState(false);
  const [goLiveError, setGoLiveError] = useState<string | null>(null);
  const [stepDown, setStepDown] = useState<'off' | 'shadow' | null>(null);

  const save = async (changes: JourneyChanges) => {
    try {
      await onSave(changes);
    } catch (err) {
      if (err instanceof ReloadFailedError) {
        setError(RELOAD_FAILED_MESSAGE);
        return;
      }
      throw err;
    }
  };

  const pick = (mode: LifecycleMode) => {
    if (mode === current || saving) return;
    setError(null);
    if (mode === 'live') {
      setGoLiveError(null);
      setGoLiveOpen(true);
      return;
    }
    setStepDown(mode);
  };

  const confirmStepDown = async () => {
    const mode = stepDown;
    if (!mode) return;
    setStepDown(null);
    setSaving(true);
    try {
      await save({ mode });
    } catch (err) {
      setError(lifecycleErrorMessage(err, 'Could not change the mode.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmLive = async () => {
    setSaving(true);
    setGoLiveError(null);
    try {
      await save({ mode: 'live' });
      setGoLiveOpen(false);
    } catch (err) {
      setGoLiveError(lifecycleErrorMessage(err, 'Could not change the mode.'));
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      await onRefresh();
    } catch (err) {
      setError(lifecycleErrorMessage(err, 'Could not refresh the stats.'));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <CardBox aria-labelledby="engineTitle" data-testid="engineCard">
      <CardHeader>
        <CardTitle id="engineTitle">Engine</CardTitle>
        {!currentAdminIsTenantAdminRole && (
          <Pill tone="grey">Read only: tenant admin changes settings</Pill>
        )}
        <Caption style={{ marginLeft: 'auto' }}>
          Stats updated {elapsedLabel(global.stats_computed_at)}
        </Caption>
        <IconButton
          aria-label="Refresh stats"
          css={{ marginLeft: 0 }}
          data-testid="statsRefreshBtn"
          disabled={refreshing}
          title="Refresh stats"
          type="button"
          onClick={refresh}
        >
          <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} />
        </IconButton>
      </CardHeader>
      <Rows>
        <RowLabel>Kill switch</RowLabel>
        <RowBody>
          <Pill tone={global.kill_switch_on ? 'green' : 'red'}>
            {global.kill_switch_on ? 'On' : 'Off'}
          </Pill>
          {!global.kill_switch_on && (
            <span style={{ color: '#B0030A', fontSize: 13 }}>
              Nothing is sent while the kill switch is off, in any mode.
            </span>
          )}
          {currentAdminIsTenantAdminRole && (
            <LinkText as={Link} to={`/${ROUTES.featureFlags}`}>
              Change in Feature Flags
            </LinkText>
          )}
        </RowBody>

        <RowLabel>Mode</RowLabel>
        <RowBody css={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <span
            title={
              currentAdminIsTenantAdminRole ? undefined : 'Tenant admin only'
            }
          >
            <PermissionComponent id="btn-lifecycle-settings" showAsReadOnly>
              <Segmented role="group" aria-label="Engine mode">
                {MODES.map(({ mode, label, testId }) => (
                  <button
                    aria-pressed={current === mode}
                    data-testid={testId}
                    disabled={!currentAdminIsTenantAdminRole || saving}
                    key={mode}
                    type="button"
                    onClick={() => pick(mode)}
                  >
                    {label}
                  </button>
                ))}
              </Segmented>
            </PermissionComponent>
          </span>
          <Caption style={{ color: '#3D3D3D' }}>{MODE_LINE[current]}</Caption>
          {error && (
            <div data-testid="engineError" role="alert">
              <Notice tone="red">{error}</Notice>
            </div>
          )}
        </RowBody>

        <PilotBudgetRows global={global} onSave={save} />

        <RowLabel>Heartbeat</RowLabel>
        <RowBody>
          <span>
            Last sweep <strong>{formatDateTime(global.last_sweep_at)}</strong>,
            last send <strong>{formatDateTime(global.last_dispatch_at)}</strong>
          </span>
          {global.heartbeat_stale && (
            <>
              <Pill tone="amber">Engine not running</Pill>
              <span
                style={{ color: '#7A5600', fontSize: 13, flexBasis: '100%' }}
              >
                No sweep or send in the last 2 hours. Messages are not going
                out.
              </span>
            </>
          )}
        </RowBody>

        <RowLabel>Waiting on us</RowLabel>
        <RowBody>
          <span>
            <strong>{global.awaiting_review_count} workers</strong> wait on a
            video review, a representative or E-Verify
          </span>
          <LinkText as={Link} to={`/${ROUTES.workersV2}`}>
            Open GravyWorkers
          </LinkText>
        </RowBody>
      </Rows>
      {goLiveOpen && (
        <GoLiveModal
          data={data}
          error={goLiveError}
          saving={saving}
          onCancel={() => setGoLiveOpen(false)}
          onConfirm={confirmLive}
        />
      )}
      {stepDown && (
        <ConfirmationModal
          disableClickout
          title={STEP_DOWN[stepDown].title}
          message={STEP_DOWN[stepDown].message}
          subMessage={
            current === 'live'
              ? 'Workers in the pilot stop getting messages.'
              : null
          }
          confirmLabel={STEP_DOWN[stepDown].confirmLabel}
          cancelLabel="Cancel"
          onCancel={() => setStepDown(null)}
          onConfirm={confirmStepDown}
        />
      )}
    </CardBox>
  );
};

export default EngineCard;
